import { ServiceClient } from '@johnlewispartnership/wtr-website-api-client';
import config from 'config';
import getApiBaseUrl from 'utils/getApiBaseUrl';
import { pathJoin } from 'utils/general';
import { logApiError, logApiInfo } from 'logger';
import {
  DeleteSlotReservationsResponseData,
  DeleteSlotReservationsResult,
  GetSlotReservationsResponseData,
  GetSlotReservationsResult,
  SlotReservations,
} from './types';

const slotOrchestrationApiUrl = getApiBaseUrl(config.services.slotOrchestration.path);

class SlotOrchestrationServiceClient extends ServiceClient {
  async getSlotReservations({
    authHeader,
    customerOrderId,
  }: {
    authHeader: string;
    customerOrderId: string;
  }): Promise<GetSlotReservationsResult> {
    const getSlotReservationsPath = `slot-reservations?customerOrderId=${customerOrderId}`;
    const response = await this.httpClient.get<GetSlotReservationsResponseData>(
      getSlotReservationsPath,
      {
        headers: {
          Authorization: authHeader,
          breadcrumb: config.applicationName,
        },
      },
    );
    const reservations = response.data;

    if (!response.error) {
      logApiInfo({
        payload: {
          duration: response.duration,
          service: 'slotOrchestration',
          url: pathJoin(slotOrchestrationApiUrl, getSlotReservationsPath),
          message: `Slot Reservations loaded from server, request took ${response.duration}ms`,
          httpStatus: String(response.status),
          httpStatusCode: response.status,
        },
      });
    } else {
      logApiError({
        payload: {
          duration: response.duration,
          service: 'slotOrchestration',
          url: pathJoin(slotOrchestrationApiUrl, getSlotReservationsPath),
          message: `Error loading slot reservations: ${response.error.message}`,
          httpStatus: String(response.status),
          httpStatusCode: response.status,
        },
      });
    }

    return {
      reservations: (reservations as SlotReservations) || [],
      error: response.error ? { message: response.error.message } : undefined,
    };
  }

  async deleteSlotReservation({
    authHeader,
    slotReservationId,
  }: {
    authHeader: string;
    slotReservationId: string;
  }): Promise<DeleteSlotReservationsResult> {
    const deleteSlotReservationPath = `slot-reservations/${slotReservationId}`;
    const response = await this.httpClient.delete<DeleteSlotReservationsResponseData>(
      deleteSlotReservationPath,
      {
        headers: {
          Authorization: authHeader,
          breadcrumb: config.applicationName,
        },
      },
    );

    if (!response.error) {
      logApiInfo({
        payload: {
          duration: response.duration,
          service: 'slotOrchestration',
          url: pathJoin(slotOrchestrationApiUrl, deleteSlotReservationPath),
          message: `Slot Reservation deleted from server, request took ${response.duration}ms`,
          httpStatus: String(response.status),
          httpStatusCode: response.status,
        },
      });
    } else {
      logApiError({
        payload: {
          duration: response.duration,
          service: 'slotOrchestration',
          url: pathJoin(slotOrchestrationApiUrl, deleteSlotReservationPath),
          message: `Error deleting slot reservation: ${response.error.message}`,
          httpStatus: String(response.status),
          httpStatusCode: response.status,
        },
      });
    }

    return {
      error: response.error ? { message: response.error.message } : undefined,
    };
  }
}

const slotOrchestrationServiceClient = new SlotOrchestrationServiceClient(slotOrchestrationApiUrl);

export default slotOrchestrationServiceClient;
